// ---------------------------------------------------------------------------
// YouAreYou (uru) — Centralized API Client
//
// Single module through which ALL API calls flow. Handles:
// - Base URL configuration
// - JWT attachment via Authorization header
// - Automatic token refresh on 401
// - Response envelope unwrapping
// - Error normalization
//
// No component should ever call fetch() directly. Use this client.
// ---------------------------------------------------------------------------

import type {
  ApiSuccess,
  ApiError,
  RefreshTokenData,
} from "@/types/api";

// ---- Configuration ----

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "https://api.youareyou.io";

// ---- Token Store ----
// Tokens are kept in memory for runtime use and persisted to localStorage
// so the session survives page reloads. The refresh token lets us get a
// fresh access token on startup without forcing the user to log in again.

const STORAGE_KEY = "uru_session";

let accessToken: string | null = null;
let refreshToken: string | null = null;
let accessTokenExpiresAt: number | null = null;

// Track in-flight refresh to prevent duplicate refresh requests
let refreshPromise: Promise<boolean> | null = null;

// ---- Token Accessors ----

/** Read the `exp` Unix timestamp from a JWT payload without verifying the signature. */
function jwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

export function setTokens(access: string, refresh: string, expiresIn: number): void {
  accessToken = access;
  refreshToken = refresh;

  // `expiresIn` may be absent (backend omits it). Fall back to the JWT's own `exp` claim.
  let ttlSeconds = expiresIn;
  if (!ttlSeconds || isNaN(ttlSeconds)) {
    const exp = jwtExp(access);
    ttlSeconds = exp ? exp - Math.floor(Date.now() / 1000) : 21600; // 6h fallback
  }
  // Store absolute expiry with a 30-second safety margin
  accessTokenExpiresAt = Date.now() + (ttlSeconds - 30) * 1000;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      accessToken,
      refreshToken,
      accessTokenExpiresAt,
    }));
  } catch {
    // localStorage unavailable (private browsing quota exceeded, etc.) — continue in-memory only
  }
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  accessTokenExpiresAt = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Restore tokens from localStorage on startup. Called once at module load time. */
function loadTokensFromStorage(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as {
      accessToken: string;
      refreshToken: string;
      accessTokenExpiresAt: number;
    };
    if (stored.accessToken && stored.refreshToken && stored.accessTokenExpiresAt) {
      accessToken = stored.accessToken;
      refreshToken = stored.refreshToken;
      accessTokenExpiresAt = stored.accessTokenExpiresAt;
    }
  } catch {
    // Corrupt or missing — start fresh
  }
}

// Restore on module load so AuthContext.hasTokens() returns true on page reload
loadTokensFromStorage();

export function getAccessToken(): string | null {
  return accessToken;
}

export function hasTokens(): boolean {
  return accessToken !== null && refreshToken !== null;
}

export function isTokenExpired(): boolean {
  if (!accessTokenExpiresAt) return true;
  return Date.now() >= accessTokenExpiresAt;
}

// ---- Event System ----
// Components can subscribe to auth events (e.g., forced logout).

type AuthEventType = "logout" | "token-refreshed";
type AuthEventListener = () => void;

const listeners = new Map<AuthEventType, Set<AuthEventListener>>();

export function onAuthEvent(event: AuthEventType, listener: AuthEventListener): () => void {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event)!.add(listener);

  // Return unsubscribe function
  return () => {
    listeners.get(event)?.delete(listener);
  };
}

function emitAuthEvent(event: AuthEventType): void {
  listeners.get(event)?.forEach((listener) => {
    try {
      listener();
    } catch {
      // Swallow listener errors — don't break the auth flow
    }
  });
}

// ---- Token Refresh ----

async function attemptTokenRefresh(): Promise<boolean> {
  if (!refreshToken) return false;

  // If a refresh is already in flight, piggyback on it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/refresh_token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        clearTokens();
        emitAuthEvent("logout");
        return false;
      }

      const json = (await response.json()) as ApiSuccess<RefreshTokenData>;

      if (json.status && json.data) {
        setTokens(
          json.data.access_token,
          json.data.refresh_token,
          json.data.expires_in,
        );
        emitAuthEvent("token-refreshed");
        return true;
      }

      clearTokens();
      emitAuthEvent("logout");
      return false;
    } catch {
      clearTokens();
      emitAuthEvent("logout");
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ---- Error Types ----

export class ApiRequestError extends Error {
  public readonly statusCode: number;
  public readonly errorCode: number | undefined;

  constructor(message: string, statusCode: number, errorCode?: number) {
    super(message);
    this.name = "ApiRequestError";
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }
}

export class NetworkError extends Error {
  constructor(message: string = "Network request failed") {
    super(message);
    this.name = "NetworkError";
  }
}

// ---- Core Request Function ----

interface RequestOptions extends Omit<RequestInit, "body"> {
  /** Skip automatic Authorization header attachment. */
  noAuth?: boolean;
  /**
   * If true, the response is returned as-is without unwrapping
   * the { status, data } envelope. Used for connection action
   * endpoints that return the entity directly.
   */
  rawResponse?: boolean;
  /** Request body — will be JSON-serialized. */
  body?: unknown;
}

/**
 * Core request function. All API calls go through here.
 *
 * By default, unwraps the standard `{ status: true, data: T }` envelope
 * and returns `T` directly. Throws `ApiRequestError` on API errors and
 * `NetworkError` on fetch failures.
 *
 * For endpoints that don't use the standard envelope (connection actions),
 * pass `rawResponse: true` to get the full parsed JSON.
 */
async function request<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { noAuth = false, rawResponse = false, body, ...fetchOptions } = options;

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;

  const headers = new Headers(fetchOptions.headers);

  // FormData bodies: let the browser set Content-Type (includes multipart boundary)
  if (body !== undefined && !(body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Attach JWT if available and not explicitly skipped
  if (!noAuth && accessToken) {
    // Proactively refresh if token is expired (but we still have a refresh token)
    if (isTokenExpired() && refreshToken) {
      const refreshed = await attemptTokenRefresh();
      if (!refreshed) {
        throw new ApiRequestError("Session expired. Please log in again.", 401);
      }
    }
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  let response: Response;

  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      body: body instanceof FormData ? body : (body !== undefined ? JSON.stringify(body) : undefined),
    });
  } catch (error) {
    throw new NetworkError(
      error instanceof Error ? error.message : "Network request failed",
    );
  }

  // Handle 401 — attempt refresh and retry once
  if (response.status === 401 && !noAuth && refreshToken) {
    const refreshed = await attemptTokenRefresh();
    if (refreshed) {
      // Retry the original request with the new token
      headers.set("Authorization", `Bearer ${accessToken}`);
      try {
        response = await fetch(url, {
          ...fetchOptions,
          headers,
          body: body instanceof FormData ? body : (body !== undefined ? JSON.stringify(body) : undefined),
        });
      } catch (error) {
        throw new NetworkError(
          error instanceof Error ? error.message : "Network request failed",
        );
      }
    }

    // If still 401 after refresh attempt, force logout
    if (response.status === 401) {
      clearTokens();
      emitAuthEvent("logout");
      throw new ApiRequestError("Session expired. Please log in again.", 401);
    }
  }

  // Parse JSON response
  let json: unknown;

  try {
    json = await response.json();
  } catch {
    throw new ApiRequestError(
      `Server returned non-JSON response (${response.status})`,
      response.status,
    );
  }

  // For raw responses (connection actions), return as-is
  if (rawResponse) {
    if (!response.ok) {
      const errBody = json as Record<string, unknown>;
      throw new ApiRequestError(
        (errBody.error as string) ?? `Request failed (${response.status})`,
        response.status,
        errBody.code as number | undefined,
      );
    }
    return json as T;
  }

  // Unwrap standard envelope
  const envelope = json as ApiSuccess<T> | ApiError;

  if (envelope.status === false) {
    throw new ApiRequestError(
      (envelope as ApiError).error,
      response.status,
      (envelope as ApiError).code,
    );
  }

  return (envelope as ApiSuccess<T>).data;
}

// ---- HTTP Method Helpers ----

export function get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
  return request<T>(endpoint, { ...options, method: "GET" });
}

export function post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(endpoint, { ...options, method: "POST", body });
}

export function put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(endpoint, { ...options, method: "PUT", body });
}

export function del<T>(endpoint: string, options?: RequestOptions): Promise<T> {
  return request<T>(endpoint, { ...options, method: "DELETE" });
}

// ---- Exported Client Object ----

export const apiClient = {
  get,
  post,
  put,
  del,
  request,
  setTokens,
  clearTokens,
  getAccessToken,
  hasTokens,
  isTokenExpired,
  onAuthEvent,
} as const;

export default apiClient;
